import { useState, type ReactNode } from "react";
import { get, post } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { YoutubePrivacy, YoutubeStatus, YoutubeUpload } from "../api/types";
import { fmtDuration } from "../lib/format";
import { openYoutubeConnect, PRIVACY, uploadMoving, useConnectResult, YT_TITLE_MAX, YT_UNVERIFIED_MAX_S } from "../lib/youtube";
import { Dot, Seg } from "./ui";

/**
 * "Post to YouTube" (redesign of 2026-10-22, docs/design/mocks/youtube/): a switch on the Upload page,
 * off on every upload, and one YouTube row on the video's status and match pages. The switch names
 * the channel; on, it asks what YouTube requires of each upload (title, who can see it, made for kids;
 * a description behind "Add a description"). Not connected, it offers Connect, which opens Google's
 * page in a new window so the chosen file stays chosen.
 */

/** What the user chose for this upload. `kids` has no answer until they pick one (YouTube asks it of every video). */
export interface PostChoice { on: boolean; title: string; titleEdited: boolean; privacy: YoutubePrivacy; kids: "" | "no" | "yes"; showDesc: boolean; description: string }

export const NO_POST: PostChoice = { on: false, title: "", titleEdited: false, privacy: "unlisted", kids: "", showDesc: false, description: "" };

/** Whether the switch can be on: a channel that takes this video. */
export function canPost(status: YoutubeStatus | null, durationS: number | null): boolean {
  const conn = status?.connection;
  return !!conn && conn.state === "active" && !tooLong(status, durationS);
}

function tooLong(status: YoutubeStatus | null, durationS: number | null): boolean {
  const conn = status?.connection;
  return !!conn && !conn.long_uploads && durationS != null && durationS > YT_UNVERIFIED_MAX_S;
}

function Switch({ on, disabled, label, onToggle }: { on: boolean; disabled?: boolean; label: string; onToggle: () => void }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-disabled={disabled || undefined} aria-labelledby={label} className="swhit"
      onClick={() => !disabled && onToggle()} data-testid="yt-switch">
      <span className={`sw${on ? " on" : ""}`} aria-hidden="true"><i /></span>
    </button>
  );
}

/** The Upload page's section. `missing`: the button was pressed with "Made for kids?" unanswered. */
export function PostToYoutube({ status, durationS, choice, onChange, missing, desktop }: {
  status: YoutubeStatus | null;
  durationS: number | null;
  choice: PostChoice;
  onChange: (next: PostChoice) => void;
  missing: boolean;
  desktop: boolean;
}) {
  const conn = status?.connection ?? null;
  const set = (patch: Partial<PostChoice>) => onChange({ ...choice, ...patch });
  let line: ReactNode = "";
  let control: ReactNode = null;
  if (status && !conn) {
    line = "Connect your channel once. Google asks you in a new window.";
    control = <button type="button" className="btn ghost yt-act" onClick={openYoutubeConnect} data-testid="yt-connect">Connect</button>;
  } else if (conn && conn.state !== "active") {
    line = `Google no longer accepts Deep Third's permission for ${conn.channel_title || "your channel"}. Connect it again to post.`;
    control = <button type="button" className="btn ghost yt-act" onClick={openYoutubeConnect} data-testid="yt-connect">Reconnect</button>;
  } else if (conn && tooLong(status, durationS)) {
    line = <>{conn.channel_title} takes videos up to 15 minutes, and this one is {fmtDuration((durationS ?? 0) * 1000)}. <a href={conn.verify_url} target="_blank" rel="noreferrer" data-testid="yt-verify">Verify the channel on YouTube</a>, then come back and switch this on.</>;
    control = <Switch on={false} disabled label="yt-h" onToggle={() => undefined} />;
  } else if (conn) {
    line = choice.on ? `${conn.channel_title} · the full video as uploaded` : conn.channel_title;
    control = <Switch on={choice.on} label="yt-h" onToggle={() => set({ on: !choice.on })} />;
  }
  const on = choice.on && canPost(status, durationS);
  const privacyNote = PRIVACY.find((p) => p.value === choice.privacy)!.note;
  const who = (
    <div className="yt-q">
      <h3 id="yt-who">Who can see it</h3>
      <div className="mt6" data-testid="yt-privacy"><Seg labelledBy="yt-who" value={choice.privacy} onChange={(v) => set({ privacy: v })} options={PRIVACY.map((p) => ({ value: p.value, label: p.label }))} /></div>
      <p className="tiny mt6">{privacyNote}</p>
    </div>
  );
  const kids = (
    <div className="yt-q">
      <h3 id="yt-kids">Made for kids?</h3>
      <div className={`mt6 yt-kids${missing ? " missing" : ""}`} data-testid="yt-kids"><Seg labelledBy="yt-kids" value={choice.kids} onChange={(v) => set({ kids: v })} options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} /></div>
      <p className="tiny mt6">YouTube asks this of every video. Match videos are usually not made for kids.</p>
    </div>
  );
  return (
    <section className="yt-sec" aria-labelledby="yt-h" data-testid="yt-section" data-on={on ? "1" : "0"}>
      <div className="row between yt-head">
        <div style={{ minWidth: 0 }}>
          <h3 id="yt-h">Post to YouTube</h3>
          <p className="tiny yt-line" data-testid="yt-line">{line}</p>
        </div>
        {control}
      </div>
      {on && (
        <div className="ytform" data-testid="yt-fields">
          <label htmlFor="yt-title">Title</label>
          <input id="yt-title" className="field" value={choice.title} maxLength={YT_TITLE_MAX + 20} onChange={(e) => set({ title: e.target.value, titleEdited: true })} data-testid="yt-title" />
          {desktop ? <div className="yt-pair">{who}{kids}</div> : <>{who}{kids}</>}
          {choice.showDesc ? (
            <>
              <label htmlFor="yt-description" className="yt-desc-l">Description</label>
              <textarea id="yt-description" className="field ta" rows={3} value={choice.description} onChange={(e) => set({ description: e.target.value })} data-testid="yt-description" />
            </>
          ) : (
            <button type="button" className="u yt-add" onClick={() => set({ showDesc: true })} data-testid="yt-add-description">Add a description</button>
          )}
          <p className="tiny yt-terms">Posting starts once we have checked the video. <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer">YouTube's Terms of Service</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google's Privacy Policy</a> apply.</p>
        </div>
      )}
    </section>
  );
}

/** Failed for a reason the user caused or chose, or that nobody can fix: a grey dot, not a red one. */
const QUIET = ["CHECK_FAILED", "UPLOAD_ABANDONED", "ORIGINAL_MISSING", "YOUTUBE_DISCONNECTED", "CANCELED"];

function dotKind(u: YoutubeUpload): "" | "on" | "ok" | "bad" {
  if (u.state === "done") return "ok";
  if (u.state === "queued" || u.state === "uploading") return "on";
  if (u.state === "failed") return QUIET.includes(u.error_code ?? "") ? "" : "bad";
  return "";
}

/** The detail line, with its "Verify ... on YouTube" words as the link to YouTube's verification page. */
function detailOf(u: YoutubeUpload): ReactNode {
  const m = u.verify_url ? /Verify (it|the channel) on YouTube/.exec(u.detail) : null;
  if (!m || !u.verify_url) return u.detail;
  return <>{u.detail.slice(0, m.index)}<a href={u.verify_url} target="_blank" rel="noreferrer" data-testid="yt-verify">{m[0]}</a>{u.detail.slice(m.index + m[0].length)}</>;
}

/**
 * The video's YouTube row (status and match pages): the newest post of this video, in the API's
 * words, with at most one action. Asked again every 5 s while it may move, every minute while it
 * waits for midnight Pacific Time. "Don't post" takes the row away and says so.
 */
export function YoutubeRow({ videoId, where, desktop, say, className = "mt24" }: { videoId: string; where: "status" | "match"; desktop: boolean; say: (msg: string) => void; className?: string }) {
  const list = useLoad(() => get<{ items: YoutubeUpload[] }>(`/v1/youtube/uploads?video_id=${encodeURIComponent(videoId)}`).catch(() => ({ items: [] as YoutubeUpload[] })), [videoId]);
  const u = list.data?.items.find((x) => x.state !== "canceled") ?? null;
  const [busy, setBusy] = useState(false);
  usePoll(list.reload, 5000, uploadMoving(u));
  usePoll(list.reload, 60_000, u?.state === "waiting_quota");
  useConnectResult((result) => {
    if (result !== "connected" || u?.action !== "reconnect") return;
    say("YouTube is connected.");
    void list.reload();
  }, () => u?.action === "reconnect" && void list.reload());
  if (!u) return null;
  const act = async (verb: "cancel" | "retry") => {
    setBusy(true);
    try {
      await post(`/v1/youtube/uploads/${u.id}/${verb}`);
      if (verb === "cancel") say("Not posting it to YouTube.");
      await list.reload();
    } catch (e) {
      say((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const full = where === "match" && desktop;
  const action =
    u.action === "cancel" ? <button type="button" className="u yt-do" onClick={() => void act("cancel")} disabled={busy} data-testid="yt-cancel">Don't post</button>
    : u.action === "retry" ? <button type="button" className="u yt-do" onClick={() => void act("retry")} disabled={busy} data-testid="yt-retry">Try again</button>
    : u.action === "reconnect" ? <button type="button" className="u yt-do" onClick={openYoutubeConnect} data-testid="yt-reconnect">Reconnect</button>
    : u.action === "open" && u.youtube_url ? <a className="yt-do yt-open" href={u.youtube_url} target="_blank" rel="noreferrer" data-testid="yt-open">{full ? "Open on YouTube" : "Open"}</a>
    : null;
  return (
    <section className={`yt-row ${where} ${className}`} aria-label="YouTube" data-testid="yt-row" data-state={u.state}>
      <div className="row">
        <Dot kind={dotKind(u)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="med" data-testid="yt-headline">{u.headline}</div>
          {u.detail && <div className="tiny" style={{ marginTop: 2 }} data-testid="yt-detail">{detailOf(u)}{full && u.state === "done" && !u.detail.includes("processing") ? " · the full video as uploaded" : ""}</div>}
        </div>
        {action}
      </div>
      {u.progress && (
        <div className="yt-bar" role="progressbar" aria-valuenow={u.progress.percent} aria-valuemin={0} aria-valuemax={100} aria-label="Posting to YouTube" data-testid="yt-bar">
          <i style={{ width: `${u.progress.percent}%` }} />
        </div>
      )}
    </section>
  );
}
